import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReplayIcon from '@mui/icons-material/Replay';

import { runSummaryAgentReportStyles } from './styles/runSummaryAgentReportStyles';
import { useEffect, useState, useMemo } from 'react';
import { runScenario } from '../../../services/appFlowsService';
import useActiveScenarioStatuses, {
  getScenarioRunKey,
} from '../../../hooks/useActiveScenarioStatuses';
import OutcomeBar from './OutcomeBar';
import { useSelector } from 'react-redux';
import { selectSelectedAppBuild } from '../../../redux/app/platformSlice';
import AIBugAnalysisDialog from '../AIBugAnalysisDialog';

const getRiskLevel = (failureRate = 0) => {
  if (failureRate >= 50) return 'high';
  if (failureRate >= 20) return 'medium';
  return 'low';
};

const getRiskLabel = (failureRate = 0, total = 0) => (total ? `${failureRate}% failed` : 'No runs');

const getDrilldownIntroCopy = ({ androidSummary, iosSummary }) => {
  if (androidSummary) {
    return {
      title: '',
      description: `Android summary: ${androidSummary}`,
    };
  }

  if (iosSummary) {
    return {
      title: '',
      description: `IOS summary: ${iosSummary}`,
    };
  }

  return {
    title: 'Failed Areas By Test Flow',
    description: 'Expand each test area to see affected scenarios, outcomes, and run time.',
  };
};

export default function RunSummaryAgentDrilldown({
  items = [],
  runs = [],
  forFailedRuns,
  androidSummary,
  iosSummary,
}) {
  const [rerunningScenarios, setRerunningScenarios] = useState({});
  const [selectedRun, setSelectedRun] = useState(null);
  const { scenarioStatusMap: activeScenarioStatuses } = useActiveScenarioStatuses();
  const drilldownIntroCopy = getDrilldownIntroCopy({ androidSummary, iosSummary });
  const appBuild = useSelector(selectSelectedAppBuild);

  const failedRunLookup = useMemo(() => {
    const lookup = {};

    runs.forEach((run) => {
      const key = `${run.flowId}-${run.scenarioId}`;

      if (!lookup[key]) {
        lookup[key] = run;
      }
    });

    return lookup;
  }, [runs]);

  useEffect(() => {
    setRerunningScenarios((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((key) => {
        if (activeScenarioStatuses[key]) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [activeScenarioStatuses]);

  const handleOnRerunPress = async (flowId, scenarioId) => {
    const key = getScenarioRunKey(flowId, scenarioId);
    if (rerunningScenarios[key] || activeScenarioStatuses[key]) return;

    try {
      setRerunningScenarios((prev) => ({
        ...prev,
        [key]: true,
      }));
      await runScenario(flowId, scenarioId);
    } catch (error) {
      console.error('Failed to rerun scenario', error);
      setRerunningScenarios((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  return (
    <Box sx={runSummaryAgentReportStyles.drilldownPanel}>
      <Box sx={runSummaryAgentReportStyles.drilldownIntro}>
        <Typography variant="subtitle2" sx={runSummaryAgentReportStyles.sectionTitle}>
          {drilldownIntroCopy.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {drilldownIntroCopy.description}
        </Typography>
      </Box>

      <Box sx={runSummaryAgentReportStyles.drilldownHeader}>
        <Box>Test Area / Scenario</Box>
        {!forFailedRuns && <Box>Runs</Box>}
        {!forFailedRuns && <Box>Passed</Box>}
        <Box>Failed</Box>
        {!forFailedRuns && <Box>Outcome</Box>}
        {!forFailedRuns && <Box>Pass Time</Box>}
        <Box>Fail Time</Box>
        <Box />
      </Box>

      <Box sx={runSummaryAgentReportStyles.drilldownBody}>
        {items.length > 0 ? (
          items.map((flow) => {
            const riskLevel = getRiskLevel(flow.failureRate);

            return (
              <Accordion
                key={`agent-drilldown-${flow.label}`}
                disableGutters
                elevation={0}
                sx={runSummaryAgentReportStyles.drilldownAccordion(riskLevel)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={runSummaryAgentReportStyles.drilldownSummary(riskLevel)}
                >
                  <Box sx={runSummaryAgentReportStyles.drilldownFlowRow}>
                    <Box sx={runSummaryAgentReportStyles.drilldownLabelCell}>
                      <Chip label="Flow" size="small" sx={runSummaryAgentReportStyles.flowChip} />
                      <Box sx={runSummaryAgentReportStyles.drilldownLabelText}>
                        <Box sx={runSummaryAgentReportStyles.drilldownTitleLine}>
                          <Typography variant="body2" noWrap title={flow.label}>
                            {flow.label}
                          </Typography>
                          {!forFailedRuns && (
                            <Chip
                              label={getRiskLabel(flow.failureRate, flow.total)}
                              size="small"
                              sx={runSummaryAgentReportStyles.riskChip(riskLevel)}
                            />
                          )}
                        </Box>
                        {!forFailedRuns && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {flow.failed || 0} of {flow.total || 0} failed -{' '}
                            {flow.affectedScenarios || 0} scenarios affected
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {!forFailedRuns && (
                      <Typography variant="body2" sx={runSummaryAgentReportStyles.totalText}>
                        {flow.total || 0}
                      </Typography>
                    )}
                    {!forFailedRuns && (
                      <Typography variant="body2" sx={runSummaryAgentReportStyles.completedText}>
                        {flow.completed || 0}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={runSummaryAgentReportStyles.failedText}>
                      {flow.failed || 0}
                    </Typography>
                    {!forFailedRuns && (
                      <OutcomeBar
                        completed={flow.completed}
                        failed={flow.failed}
                        total={flow.total}
                      />
                    )}
                    {!forFailedRuns && (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                    {!forFailedRuns && (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={runSummaryAgentReportStyles.drilldownDetails}>
                  {(flow.scenarios || []).length > 0 ? (
                    (flow.scenarios || []).map((scenario) => {
                      const scenarioRiskLevel = getRiskLevel(scenario.failureRate);
                      const rerunKey = getScenarioRunKey(scenario?.flowId, scenario?.scenarioId);
                      const isRerunning =
                        Boolean(rerunningScenarios[rerunKey]) ||
                        Boolean(activeScenarioStatuses[rerunKey]);
                      const canRerun = Boolean(scenario?.flowId && scenario?.scenarioId);
                      const failedRun =
                        failedRunLookup[`${scenario.flowId}-${scenario.scenarioId}`];
                      return (
                        <Box
                          key={`${flow.label}-${scenario.label}`}
                          sx={runSummaryAgentReportStyles.drilldownScenarioRow(scenarioRiskLevel)}
                        >
                          <Box sx={runSummaryAgentReportStyles.drilldownLabelCell}>
                            <Chip
                              label="Scenario"
                              size="small"
                              sx={runSummaryAgentReportStyles.scenarioChip}
                            />
                            <Box sx={runSummaryAgentReportStyles.drilldownLabelText}>
                              <Box sx={runSummaryAgentReportStyles.drilldownTitleLine}>
                                <Typography variant="body2" noWrap title={scenario.label}>
                                  {scenario.label}
                                </Typography>
                                {!forFailedRuns && (
                                  <Chip
                                    label={getRiskLabel(scenario.failureRate, scenario.total)}
                                    size="small"
                                    sx={runSummaryAgentReportStyles.riskChip(scenarioRiskLevel)}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                          {!forFailedRuns && (
                            <Typography variant="body2" sx={runSummaryAgentReportStyles.totalText}>
                              {scenario.total || 0}
                            </Typography>
                          )}
                          {!forFailedRuns && (
                            <Typography
                              variant="body2"
                              sx={runSummaryAgentReportStyles.completedText}
                            >
                              {scenario.completed || 0}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={runSummaryAgentReportStyles.failedText}>
                            {scenario.failed || 0}
                          </Typography>
                          {!forFailedRuns && (
                            <OutcomeBar
                              completed={scenario.completed}
                              failed={scenario.failed}
                              total={scenario.total}
                            />
                          )}
                          {!forFailedRuns && (
                            <Typography
                              variant="body2"
                              sx={runSummaryAgentReportStyles.durationText('passed')}
                            >
                              {scenario.passedDuration}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={runSummaryAgentReportStyles.durationText('failed')}
                          >
                            {scenario.failedDuration}
                          </Typography>
                          {forFailedRuns && (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                minWidth: 210,
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  minWidth: 100,
                                  height: 32,
                                  whiteSpace: 'nowrap',
                                }}
                                startIcon={
                                  isRerunning ? <CircularProgress size={14} /> : <ReplayIcon />
                                }
                                onClick={() =>
                                  handleOnRerunPress(scenario?.flowId, scenario?.scenarioId)
                                }
                                disabled={isRerunning || !canRerun || !appBuild}
                              >
                                Rerun
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={!failedRun}
                                onClick={() => {
                                  setSelectedRun(failedRun);
                                }}
                                sx={{
                                  textTransform: 'uppercase',
                                  fontSize: '0.72rem',
                                  px: 1.5,
                                  py: 0.4,
                                  minWidth: 120,
                                  height: 32,
                                  borderRadius: 1,
                                  fontWeight: 600,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Analyze Bug
                              </Button>
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={runSummaryAgentReportStyles.drilldownEmptyScenario}
                    >
                      No scenario data available for this flow.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Box sx={runSummaryAgentReportStyles.drilldownEmptyState}>
            <Typography variant="body2" color="text.secondary">
              No flow or scenario drilldown data available.
            </Typography>
          </Box>
        )}
      </Box>
      <AIBugAnalysisDialog
        open={Boolean(selectedRun)}
        run={selectedRun}
        onClose={() => setSelectedRun(null)}
      />
    </Box>
  );
}
